// src/pages/UserProfilePage.tsx
// Page de profil utilisateur - similaire à l'app mobile
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Avatar,
    Button,
    Card,
    CardContent,
    Stack,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkIcon from '@mui/icons-material/Link';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VerifiedIcon from '@mui/icons-material/Verified';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import ArticleIcon from '@mui/icons-material/Article';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';

// Types
interface UserProfile {
    user: {
        id: number;
        name: string;
        email?: string;
        created_at: string;
        avatar_url?: string;
        cover_url?: string;
    };
    profile: {
        bio?: string;
        location?: string;
        website?: string;
        avatar_url?: string;
        cover_url?: string;
        is_verified?: boolean;
        is_public?: boolean;
        followers_count: number;
        following_count: number;
        posts_count: number;
    };
    stats?: {
        posts_count: number;
        comments_count?: number;
    };
    relationship?: {
        is_following: boolean;
        is_followed_by: boolean;
    };
    recent_posts: Array<{
        id: number;
        title?: string;
        content: string;
        post_type: string;
        created_at: string;
        likes_count?: number;
        comments_count?: number;
        media?: Array<{
            id: number;
            file_url: string;
            media_type: string;
        }>;
    }>;
    is_private?: boolean;
}

const UserProfilePage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const { authState } = useAuth();
    const currentUser = authState.user;
    const { success: showSuccess, error: showError } = useNotification();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [profileData, setProfileData] = useState<UserProfile | null>(null);
    const [isFollowing, setIsFollowing] = useState(false);
    const [followLoading, setFollowLoading] = useState(false);

    const isSelf = currentUser?.id === Number(userId);

    // Charger le profil
    const loadProfile = async () => {
        if (!userId) return;

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(`/profile/${userId}`);
            setProfileData(response.data);
            setIsFollowing(response.data.relationship?.is_following || false);
        } catch (err: any) {
            console.error('Error loading profile:', err);
            setError(err.response?.data?.detail || 'Erreur lors du chargement du profil');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    // Toggle follow
    const handleFollowToggle = async () => {
        if (!userId || followLoading) return;

        setFollowLoading(true);
        try {
            if (isFollowing) {
                await api.delete(`/social/follow/${userId}`);
                setIsFollowing(false);
                if (profileData?.profile) {
                    setProfileData({
                        ...profileData,
                        profile: {
                            ...profileData.profile,
                            followers_count: Math.max(0, profileData.profile.followers_count - 1),
                        },
                    });
                }
                showSuccess('Vous ne suivez plus cet utilisateur');
            } else {
                await api.post(`/social/follow/${userId}`);
                setIsFollowing(true);
                if (profileData?.profile) {
                    setProfileData({
                        ...profileData,
                        profile: {
                            ...profileData.profile,
                            followers_count: profileData.profile.followers_count + 1,
                        },
                    });
                }
                showSuccess('Vous suivez maintenant cet utilisateur');
            }
        } catch (err: any) {
            showError(err.response?.data?.detail || 'Erreur');
        } finally {
            setFollowLoading(false);
        }
    };

    // Format date
    const formatJoinDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
    };

    const formatPostDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffDays < 1) return "Aujourd'hui";
        if (diffDays < 7) return `Il y a ${diffDays}j`;
        return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    };

    // Loading state
    if (loading) {
        return (
            <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2, mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <Skeleton variant="circular" width={100} height={100} />
                    <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={32} />
                        <Skeleton variant="text" width="40%" />
                    </Box>
                </Box>
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Box>
        );
    }

    // Error state
    if (error) {
        return (
            <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
                    Retour
                </Button>
                <Alert
                    severity="error"
                    action={
                        <Button color="inherit" size="small" onClick={loadProfile}>
                            Réessayer
                        </Button>
                    }
                >
                    {error}
                </Alert>
            </Box>
        );
    }

    if (!profileData) return null;

    const { user, profile, stats, recent_posts } = profileData;
    const avatarUrl = user?.avatar_url || profile?.avatar_url;
    const coverUrl = user?.cover_url || profile?.cover_url;
    const isPrivate = profileData.is_private && !isFollowing && !isSelf;

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', pb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, gap: 2 }}>
                <IconButton onClick={() => navigate(-1)}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
                    {user?.name || 'Profil'}
                </Typography>
                <IconButton onClick={loadProfile}>
                    <RefreshIcon />
                </IconButton>
            </Box>

            {/* Cover & Avatar */}
            <Box sx={{ position: 'relative', mb: 8 }}>
                {/* Cover Image */}
                <Box
                    sx={{
                        height: 150,
                        bgcolor: coverUrl ? 'transparent' : 'primary.light',
                        backgroundImage: coverUrl ? `url(${coverUrl})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: 2,
                        mx: 2,
                    }}
                />

                {/* Avatar */}
                <Avatar
                    src={avatarUrl}
                    sx={{
                        width: 100,
                        height: 100,
                        position: 'absolute',
                        bottom: -50,
                        left: 32,
                        border: '4px solid white',
                        bgcolor: 'primary.main',
                        fontSize: '2.5rem',
                    }}
                >
                    {user?.name?.charAt(0)?.toUpperCase()}
                </Avatar>

                {/* Verified Badge */}
                {profile?.is_verified && (
                    <VerifiedIcon
                        sx={{
                            position: 'absolute',
                            bottom: -45,
                            left: 110,
                            color: 'primary.main',
                            bgcolor: 'white',
                            borderRadius: '50%',
                        }}
                    />
                )}
            </Box>

            {/* User Info */}
            <Box sx={{ px: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {user?.name}
                            {profile?.is_verified && (
                                <VerifiedIcon sx={{ ml: 1, color: 'primary.main', fontSize: 20 }} />
                            )}
                        </Typography>
                        {user?.email && (
                            <Typography variant="body2" color="text.secondary">
                                {user.email}
                            </Typography>
                        )}
                    </Box>

                    {/* Follow Button */}
                    {!isSelf && (
                        <Button
                            variant={isFollowing ? 'outlined' : 'contained'}
                            startIcon={isFollowing ? <PersonRemoveIcon /> : <PersonAddIcon />}
                            onClick={handleFollowToggle}
                            disabled={followLoading}
                            sx={{ borderRadius: 5 }}
                        >
                            {followLoading ? (
                                <CircularProgress size={20} />
                            ) : isFollowing ? (
                                'Ne plus suivre'
                            ) : (
                                'Suivre'
                            )}
                        </Button>
                    )}
                </Stack>

                {/* Bio */}
                {profile?.bio && (
                    <Typography variant="body1" sx={{ mt: 2, lineHeight: 1.6 }}>
                        {profile.bio}
                    </Typography>
                )}

                {/* Meta Info */}
                <Stack direction="row" spacing={3} sx={{ mt: 2, flexWrap: 'wrap' }}>
                    {profile?.location && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                {profile.location}
                            </Typography>
                        </Stack>
                    )}
                    {profile?.website && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography
                                variant="body2"
                                color="primary"
                                component="a"
                                href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                                target="_blank"
                                sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                {profile.website}
                            </Typography>
                        </Stack>
                    )}
                    {user?.created_at && (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                            <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                                Membre depuis {formatJoinDate(user.created_at)}
                            </Typography>
                        </Stack>
                    )}
                </Stack>

                {/* Stats */}
                <Stack
                    direction="row"
                    spacing={4}
                    sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}>
                        <Typography variant="h6" fontWeight={700}>
                            {profile?.followers_count || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Abonnés
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', flex: 1, cursor: 'pointer' }}>
                        <Typography variant="h6" fontWeight={700}>
                            {profile?.following_count || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Abonnements
                        </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box sx={{ textAlign: 'center', flex: 1 }}>
                        <Typography variant="h6" fontWeight={700}>
                            {stats?.posts_count || profile?.posts_count || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Posts
                        </Typography>
                    </Box>
                </Stack>

                {/* Relationship Badge */}
                {profileData.relationship?.is_followed_by && !isSelf && (
                    <Chip
                        label="Vous suit"
                        size="small"
                        sx={{ mt: 2 }}
                        color="primary"
                        variant="outlined"
                    />
                )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Recent Posts */}
            <Box sx={{ px: 3 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                    Publications récentes
                </Typography>

                {isPrivate ? (
                    <Card sx={{ textAlign: 'center', py: 6 }}>
                        <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Ce profil est privé
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Suivez cet utilisateur pour voir ses publications
                        </Typography>
                    </Card>
                ) : recent_posts && recent_posts.length > 0 ? (
                    <Stack spacing={2}>
                        {recent_posts.map((post) => (
                            <Card
                                key={post.id}
                                sx={{
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { bgcolor: 'grey.50', transform: 'translateY(-2px)' },
                                }}
                                onClick={() => navigate(`/post/${post.id}`)}
                            >
                                <CardContent>
                                    <Stack direction="row" spacing={2}>
                                        {/* Post Image Preview */}
                                        {post.media && post.media.length > 0 && post.media[0].media_type === 'image' && (
                                            <Box
                                                sx={{
                                                    width: 80,
                                                    height: 80,
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <img
                                                    src={post.media[0].file_url}
                                                    alt=""
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            </Box>
                                        )}

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            {post.title && (
                                                <Typography variant="subtitle1" fontWeight={600} noWrap>
                                                    {post.title}
                                                </Typography>
                                            )}
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                }}
                                            >
                                                {post.content}
                                            </Typography>
                                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatPostDate(post.created_at)}
                                                </Typography>
                                                {post.likes_count !== undefined && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {post.likes_count} like{post.likes_count !== 1 ? 's' : ''}
                                                    </Typography>
                                                )}
                                                {post.comments_count !== undefined && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {post.comments_count} commentaire{post.comments_count !== 1 ? 's' : ''}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                ) : (
                    <Card sx={{ textAlign: 'center', py: 6 }}>
                        <ArticleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary">
                            Aucune publication récente
                        </Typography>
                    </Card>
                )}
            </Box>
        </Box>
    );
};

export default UserProfilePage;
